import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../../../lib/utils';
import { Button } from '../../ui/catalyst/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/catalyst/table';
import { Text } from '../../ui/catalyst/text';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '../../ui/context-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { PaginationControls } from '../PaginationControls';
import { ClickableCell } from './ClickableCell';
import { ColumnFilter } from './ColumnFilter';
import { DataTableHeader } from './DataTableHeader';
import { useTableKeyboardNavigation } from './hooks/useTableKeyboardNavigation';
import { SkeletonTable } from './SkeletonTable';
import { SortOnlyDropdown } from './SortOnlyDropdown';
import { DataTableProps, TableColumn } from './types';

/**
 * Intelligently determine default width for a column based on its characteristics.
 * Returns null if column should flex to fill remaining space.
 */
const getDefaultColumnWidth = (
  column: TableColumn<unknown>,
  columnIndex: number,
): string | null => {
  // If width explicitly set, use it (allows override)
  if (column.width) return column.width;

  // First column with no width = flex to fill space
  if (columnIndex === 0) return null;

  const headerLower =
    typeof column.header === 'string' ? column.header.toLowerCase() : '';

  // Date columns - consistent format MM/DD/YYYY
  if (
    headerLower.includes('date') ||
    headerLower.includes('created') ||
    headerLower.includes('modified')
  ) {
    return '140px';
  }

  // Status/state columns - pills or short text
  if (
    headerLower.includes('status') ||
    headerLower.includes('state') ||
    column.filter?.type === 'multiselect'
  ) {
    return '180px';
  }

  // Note/count columns - typically just numbers
  if (headerLower.includes('notes') || headerLower.includes('count')) {
    return '100px';
  }

  // Activity columns - "X days ago" format
  if (headerLower.includes('activity')) {
    return '150px';
  }

  // Default for other columns with no width
  return '200px';
};

/**
 * Generic, reusable data table component
 *
 * Features:
 * - Configurable columns with sorting
 * - Search functionality
 * - Custom actions and menu buttons
 * - Pagination support
 * - Loading and empty states
 * - Fully typed with TypeScript
 * - Automatic column width defaults (can be overridden)
 * - Fixed table layout prevents column jumping
 *
 * @example
 * ```tsx
 * <DataTable
 *   data={intakes}
 *   columns={intakeColumns}
 *   searchEnabled
 *   searchValue={search}
 *   onSearchChange={setSearch}
 *   actions={[
 *     { label: 'Reset view', onClick: handleReset },
 *     { label: 'New Intake', onClick: handleCreate, color: 'blue' }
 *   ]}
 *   paginationEnabled
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   totalCount={totalCount}
 *   onPageChange={setPage}
 * />
 * ```
 */
export const DataTable = <TData,>({
  data,
  columns,
  searchEnabled = false,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  searchResultsText,
  searchFields = [],
  disableClientFilter = false,
  actions = [],
  menuActions = [],
  paginationEnabled = false,
  currentPage = 1,
  totalPages = 1,
  totalCount = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  sortField,
  sortDirection = 'asc',
  onFilterChange,
  onDateRangeFilterChange,
  filterValues = {},
  isLoading = false,
  showProgressBar = false,
  emptyMessage = 'No data available',
  emptyStateConfig,
  onRowClick,
  onRowHover,
  onRowContextMenu,
  getRowKey,
  getRowClassName,
  title,
  description,
  selectionEnabled = false,
  selectedRows: controlledSelectedRows,
  onSelectionChange,
  bulkActions = [],
  density = 'compact',
  grouping,
}: DataTableProps<TData>) => {
  // Internal selection state (used when not controlled)
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string>>(
    new Set(),
  );
  const selectedRows = controlledSelectedRows ?? internalSelectedRows;
  const setSelectedRows = onSelectionChange ?? setInternalSelectedRows;

  // Track last clicked index for shift-select range (checkbox selection only)
  const lastClickedIndexRef = useRef<number | null>(null);
  // Ref for scrollable container (used for keyboard navigation)
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Column resizing state
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});
  const resizeStateRef = useRef<{
    columnId: string;
    startX: number;
    startWidth: number;
  } | null>(null);

  // Clear selection when page changes
  useEffect(() => {
    if (paginationEnabled) {
      setSelectedRows(new Set());
      lastClickedIndexRef.current = null;
    }
  }, [currentPage, paginationEnabled, setSelectedRows]);

  /**
   * Client-side search filtering - filters current page data only.
   * Search is NOT sent to backend API to keep implementation simple.
   */
  const filteredData = useMemo(() => {
    // No filtering if search is disabled or no fields specified
    if (
      !searchValue ||
      !searchEnabled ||
      searchFields.length === 0 ||
      disableClientFilter
    ) {
      return data;
    }

    const searchLower = searchValue.toLowerCase().trim();
    return data.filter((row) => {
      // Match if ANY searchable field contains the search term (case-insensitive)
      return searchFields.some((field) => {
        const value = (row as Record<string, unknown>)[field];
        if (value == null) return false;
        return String(value).toLowerCase().includes(searchLower);
      });
    });
  }, [data, searchValue, searchEnabled, searchFields, disableClientFilter]);

  // Generate row key (needed by selection handlers)
  const getKey = (row: TData, index: number): string => {
    if (getRowKey) {
      return getRowKey(row);
    }
    if ((row as Record<string, unknown>).id) {
      return String((row as Record<string, unknown>).id);
    }
    return `row-${index}`;
  };

  // Keyboard navigation support (independent from checkbox selection)
  const keyboardNav = useTableKeyboardNavigation({
    data: filteredData,
    onRowClick,
    containerRef: tableContainerRef,
    enabled: !!onRowClick && filteredData.length > 0,
  });
  const { handleKeyDown, setActiveIndexFromClick } = keyboardNav;

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allKeys = new Set(
        filteredData.map((row, index) => getKey(row, index)),
      );
      setSelectedRows(allKeys);
    } else {
      setSelectedRows(new Set());
    }
    lastClickedIndexRef.current = null;
  };

  const handleRowSelect = (
    rowKey: string,
    rowIndex: number,
    shiftKey: boolean,
  ) => {
    const newSelection = new Set(selectedRows);

    if (shiftKey && lastClickedIndexRef.current !== null) {
      // Shift-click: select range
      const start = Math.min(lastClickedIndexRef.current, rowIndex);
      const end = Math.max(lastClickedIndexRef.current, rowIndex);

      for (let i = start; i <= end; i += 1) {
        const key = getKey(filteredData[i], i);
        newSelection.add(key);
      }
    } else {
      // Normal click: toggle single row
      if (newSelection.has(rowKey)) {
        newSelection.delete(rowKey);
      } else {
        newSelection.add(rowKey);
      }
      lastClickedIndexRef.current = rowIndex;
    }

    setSelectedRows(newSelection);
  };

  const handleClearSelection = () => {
    setSelectedRows(new Set());
    lastClickedIndexRef.current = null;
  };

  // Column resize handlers
  const handleResizeStart = (
    e: React.MouseEvent,
    columnId: string,
    currentWidth: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Parse current width to pixels
    const widthInPx = parseInt(currentWidth, 10);

    resizeStateRef.current = {
      columnId,
      startX: e.clientX,
      startWidth: widthInPx,
    };

    // Add global mouse event listeners
    // Use handleResizeEndRef.current to ensure we remove the same listener we add
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEndRef.current);
  };

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!resizeStateRef.current) return;

    const { columnId, startX, startWidth } = resizeStateRef.current;
    const deltaX = e.clientX - startX;
    const newWidth = Math.max(100, startWidth + deltaX); // Min width 100px

    setColumnWidths((prev) => ({
      ...prev,
      [columnId]: `${newWidth}px`,
    }));
  }, []);

  const handleResizeEndRef = useRef<() => void>(() => {});

  const handleResizeEnd = useCallback(() => {
    resizeStateRef.current = null;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEndRef.current);
  }, [handleResizeMove]);

  // Update ref whenever handler changes
  useEffect(() => {
    handleResizeEndRef.current = handleResizeEnd;
  }, [handleResizeEnd]);

  // Cleanup resize listeners on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEndRef.current);
    };
  }, [handleResizeMove]);

  // Calculate results display range
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalCount);

  /**
   * Results text displays filtered count when search is active,
   * otherwise shows pagination range (e.g., "Showing 1-50 of 499 results")
   */
  const displayResultsText = useMemo(() => {
    if (searchValue && searchEnabled && filteredData.length !== data.length) {
      // Search is active and filtering results - show clean count
      const count = filteredData.length;
      return `${count} result${count === 1 ? '' : 's'} found`;
    }
    // Use provided searchResultsText or compute default
    return (
      searchResultsText ||
      (totalCount > 0
        ? `Showing ${startIndex}-${endIndex} of ${totalCount} results`
        : undefined)
    );
  }, [
    searchValue,
    searchEnabled,
    filteredData.length,
    data.length,
    searchResultsText,
    totalCount,
    startIndex,
    endIndex,
  ]);

  // Handle column sort
  const handleSort = (columnId: string) => {
    onSortChange?.(columnId);
  };

  // Get sort direction for a specific column (for unified filter)
  // For multi-option sorting, also checks if sortField matches any of the sort options
  const getSortDirection = (
    columnId: string,
    sortOptions?: Array<{ key: string }>,
  ): 'asc' | 'desc' | null => {
    // Check if sortField matches the column ID directly
    if (sortField === columnId) return sortDirection;
    // For multi-option sorting, check if sortField matches any option key
    if (sortOptions?.some((opt) => opt.key === sortField)) {
      return sortDirection;
    }
    return null;
  };

  // Handle sort change from unified filter dropdown
  // For multi-option sorting, sortKey overrides columnId as the ordering field
  const handleSortChangeFromFilter = (
    columnId: string,
    direction: 'asc' | 'desc',
    sortKey?: string,
  ) => {
    // Use sortKey if provided (for multi-option sorting), otherwise use columnId
    const orderingField = sortKey || columnId;
    // Pass explicit direction to parent - enables toggle-off when clicking same option
    onSortChange?.(orderingField, direction);
  };

  // Get cell value from row
  const getCellValue = (row: TData, column: (typeof columns)[0]) => {
    if (column.accessorFn) {
      return column.accessorFn(row);
    }
    if (column.accessorKey) {
      return (row as Record<string, unknown>)[column.accessorKey];
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-zinc-900 relative flex flex-col flex-1 min-h-0">
      {/* Header with search and actions */}
      <div className="flex-shrink-0">
        <DataTableHeader
          title={title}
          description={description}
          searchEnabled={searchEnabled}
          searchPlaceholder={searchPlaceholder}
          searchValue={searchValue}
          onSearchChange={onSearchChange}
          searchResultsText={displayResultsText}
          actions={actions}
          menuActions={menuActions}
          totalCount={totalCount}
          startIndex={startIndex}
          endIndex={endIndex}
          selectionEnabled={selectionEnabled}
          selectedCount={selectedRows.size}
          totalSelectable={filteredData.length}
          onClearSelection={handleClearSelection}
          bulkActions={bulkActions}
          isLoading={isLoading}
          showProgressBar={showProgressBar}
        />
      </div>

      {/* Table - wrapped in scrollable div to apply table-layout: fixed */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={tableContainerRef}
        tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        role="region"
        aria-label="Data Table"
        onKeyDown={handleKeyDown}
        className={`[&_table]:table-fixed [&_table]:w-full overflow-auto flex-1 outline-none relative transition-opacity duration-200 ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}
        style={{ contain: 'layout' }}
      >
        <Table
          className="[--gutter:--spacing(6)] !overflow-x-visible"
          dense={density === 'compact'}
        >
          <TableHead className={density === 'compact' ? 'h-8' : 'h-10'}>
            <TableRow>
              {/* Checkbox column for row selection */}
              {selectionEnabled && (
                <TableHeader
                  className="w-12 !pl-4"
                  style={
                    density === 'compact'
                      ? { paddingTop: '4px', paddingBottom: '4px' }
                      : undefined
                  }
                >
                  <input
                    type="checkbox"
                    checked={
                      filteredData.length > 0 &&
                      selectedRows.size === filteredData.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 dark:border-zinc-700 text-blue-600 focus:ring-blue-500 dark:bg-zinc-800 cursor-pointer"
                    aria-label="Select all rows"
                  />
                </TableHeader>
              )}
              {columns.map((column, columnIndex) => {
                // Apply intelligent default width if not explicitly set
                const defaultWidth = getDefaultColumnWidth(
                  column as TableColumn<unknown>,
                  columnIndex,
                );
                // Use resized width if available, otherwise use default
                const columnWidth = columnWidths[column.id] || defaultWidth;

                // Build text alignment class - same for headers and cells
                let textAlign = 'text-left'; // Default
                if (column.align === 'center') {
                  textAlign = 'text-center';
                } else if (column.align === 'right') {
                  textAlign = 'text-right';
                }

                // Build header className
                // Only show pointer cursor for simple sortable columns (no filter, no multi-options)
                const isSimpleSortable =
                  column.sortable && !column.filter && !column.sort?.options;
                const headerClassName = `${
                  isSimpleSortable ? 'cursor-pointer select-none' : ''
                } font-semibold ${density === 'compact' ? 'leading-4 !py-1' : 'leading-6'} text-black dark:text-white ${column.resizable ? 'relative' : ''} ${textAlign} ${columnIndex === 0 && !selectionEnabled ? '!pl-4' : ''}`;

                // Use sortKey if defined, otherwise fall back to column.id
                const sortKey = column.sort?.sortKey || column.id;

                return (
                  <TableHeader
                    key={column.id}
                    className={headerClassName}
                    onClick={() =>
                      isSimpleSortable ? handleSort(sortKey) : undefined
                    }
                    style={{
                      ...(columnWidth
                        ? { width: columnWidth, minWidth: columnWidth }
                        : {}),
                      ...(density === 'compact'
                        ? { paddingTop: '4px', paddingBottom: '4px' }
                        : {}),
                    }}
                  >
                    {/* If column has a filter, render ColumnFilter component */}
                    {column.filter && onFilterChange ? (
                      <ColumnFilter
                        columnId={column.id}
                        header={column.header}
                        filter={column.filter}
                        value={filterValues[column.id]}
                        onChange={onFilterChange}
                        sortable={column.sortable}
                        sortDirection={getSortDirection(
                          sortKey,
                          column.sort?.options,
                        )}
                        sortField={sortField}
                        onSortChange={(_, direction, optionSortKey) =>
                          handleSortChangeFromFilter(
                            sortKey,
                            direction,
                            optionSortKey,
                          )
                        }
                        sortLabels={column.sort?.labels}
                        invertArrows={column.sort?.invertArrows}
                        sortOptions={column.sort?.options}
                        onDateRangeFilterChange={onDateRangeFilterChange}
                        allFilterValues={filterValues || {}}
                      />
                    ) : column.sortable && column.sort?.options ? (
                      // Sortable column with multiple options but no filter
                      <SortOnlyDropdown
                        header={column.header}
                        sortOptions={column.sort.options}
                        sortField={sortField}
                        sortDirection={sortDirection}
                        onSortChange={(direction, optionSortKey) =>
                          handleSortChangeFromFilter(
                            sortKey,
                            direction,
                            optionSortKey,
                          )
                        }
                      />
                    ) : (
                      <div className="inline-flex items-center gap-1">
                        {column.headerTooltip ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">
                                {column.header}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              <p className="text-xs">{column.headerTooltip}</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <span>{column.header}</span>
                        )}
                        {column.sortable &&
                          (sortField === sortKey ||
                            column.sort?.options?.some(
                              (opt) => opt.key === sortField,
                            )) && (
                            <span className="text-blue-600 dark:text-blue-400">
                              {sortDirection === 'asc' ? (
                                <ArrowUpIcon className="h-4 w-4" />
                              ) : (
                                <ArrowDownIcon className="h-4 w-4" />
                              )}
                            </span>
                          )}
                      </div>
                    )}
                    {/* Resize handle for resizable columns */}
                    {column.resizable && (
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                      <div
                        className="absolute right-0 top-0 bottom-0 w-3 cursor-col-resize group flex items-center justify-center"
                        onMouseDown={(e) =>
                          handleResizeStart(
                            e,
                            column.id,
                            columnWidth || '200px',
                          )
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-[3px] h-4 bg-zinc-300 dark:bg-zinc-600 group-hover:bg-blue-500 transition-colors" />
                      </div>
                    )}
                  </TableHeader>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading && filteredData.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectionEnabled ? 1 : 0)}
                  className="p-0 border-none h-full align-top"
                >
                  {/* Skeleton Table for initial load when no data is available yet */}
                  <SkeletonTable
                    columns={columns}
                    rowCount={Math.min(pageSize, 10)}
                    selectionEnabled={selectionEnabled}
                    density={density}
                  />
                </TableCell>
              </TableRow>
            )}

            {filteredData.length > 0 &&
              (() => {
                // Compute group boundaries for group header rendering
                const groupKeys: string[] = grouping
                  ? filteredData.map((row) => grouping.getGroupKey(row))
                  : [];

                return filteredData.map((row, rowIndex) => {
                const rowKey = getKey(row, rowIndex);
                const isSelected = selectedRows.has(rowKey);

                // Determine if this row starts a new group
                const isNewGroup =
                  grouping &&
                  (rowIndex === 0 ||
                    groupKeys[rowIndex] !== groupKeys[rowIndex - 1]);

                // Count rows in this group (for suffix)
                let groupRowCount = 0;
                if (isNewGroup && grouping) {
                  const currentGroupKey = groupKeys[rowIndex];
                  for (let i = rowIndex; i < groupKeys.length; i += 1) {
                    if (groupKeys[i] === currentGroupKey) {
                      groupRowCount += 1;
                    } else {
                      break;
                    }
                  }
                }

                // Build base className
                const rowHeight = density === 'compact' ? 'h-6' : 'h-12';
                const baseClassName = onRowClick
                  ? `cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors ${rowHeight}`
                  : rowHeight;

                // Add custom className if provided (merged with cn for better specificity handling)
                const finalClassName = cn(
                  baseClassName,
                  getRowClassName?.(row, rowIndex),
                );

                // Get context menu items for this row
                const contextMenuItems = onRowContextMenu?.(row);

                // Group header row (rendered before the first row of each group)
                const groupHeader = isNewGroup ? (
                  <TableRow
                    key={`group-${rowIndex}-${groupKeys[rowIndex]}`}
                    className="bg-zinc-100 dark:bg-zinc-800/60"
                  >
                    <TableCell
                      colSpan={columns.length + (selectionEnabled ? 1 : 0)}
                      className="!py-1.5 !pl-4"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                          {grouping.getGroupLabel
                            ? grouping.getGroupLabel(groupKeys[rowIndex])
                            : groupKeys[rowIndex]}
                        </span>
                        <span className="text-xs text-zinc-500 dark:text-zinc-400">
                          {grouping.getGroupSuffix
                            ? grouping.getGroupSuffix(
                                groupKeys[rowIndex],
                                filteredData.slice(
                                  rowIndex,
                                  rowIndex + groupRowCount,
                                ),
                              )
                            : `${groupRowCount} ${groupRowCount === 1 ? 'item' : 'items'}`}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : null;

                const rowContent = (
                  <TableRow
                    key={rowKey}
                    className={finalClassName}
                    onMouseEnter={
                      onRowHover ? () => onRowHover(row) : undefined
                    }
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest('[data-stop-row-click="true"]')) {
                        return;
                      }
                      // Don't trigger row click when clicking checkbox
                      if (
                        selectionEnabled &&
                        target.tagName === 'INPUT' &&
                        (target as HTMLInputElement).type === 'checkbox'
                      ) {
                        return;
                      }
                      onRowClick?.(row);
                      setActiveIndexFromClick(rowIndex);
                      // Keep focus on table container for keyboard navigation
                      tableContainerRef.current?.focus({ preventScroll: true });
                    }}
                  >
                    {/* Checkbox cell for row selection */}
                    {selectionEnabled && (
                      <TableCell
                        className="!pl-4"
                        style={
                          density === 'compact'
                            ? { paddingTop: '4px', paddingBottom: '4px' }
                            : undefined
                        }
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowSelect(rowKey, rowIndex, e.shiftKey);
                          }}
                          onChange={() => {
                            // onChange required for controlled checkbox, but logic is in onClick
                          }}
                          className="h-4 w-4 rounded border-gray-300 dark:border-zinc-700 text-blue-600 focus:ring-blue-500 dark:bg-zinc-800 cursor-pointer"
                          aria-label={`Select row ${rowIndex + 1}`}
                        />
                      </TableCell>
                    )}
                    {columns.map((column, columnIndex) => {
                      const value = getCellValue(row, column);
                      const shouldTruncate = column.truncate !== false;
                      const hasCustomRenderer = !!column.cell;
                      const hasClickableConfig = !!column.clickable;

                      // Build cell className with overflow handling
                      // Use tighter line height in compact mode
                      let cellClassName =
                        density === 'compact'
                          ? 'leading-4 font-medium'
                          : 'leading-6 font-medium';
                      // Explicitly set text alignment to match header
                      if (column.align === 'center') {
                        cellClassName += ' text-center';
                      } else if (column.align === 'right') {
                        cellClassName += ' text-right';
                      } else {
                        cellClassName += ' text-left';
                      }
                      // IMPORTANT: Add max-width: 0 to enable truncation in table cells
                      // This works because table-layout: fixed distributes width,
                      // and max-width: 0 tells the cell to respect that width
                      cellClassName += ' max-w-0';
                      // In compact mode, override Catalyst's py-1.5 with py-1 for tighter spacing
                      if (density === 'compact') {
                        cellClassName += ' !py-1';
                      }

                      // Add left padding to first column if selection is disabled
                      if (columnIndex === 0 && !selectionEnabled) {
                        cellClassName += ' !pl-4';
                      }
                      // Only add truncate wrapper + title for simple text cells (no custom renderer)
                      // Custom renderers handle their own tooltips (e.g., Intake's Notes, Referral)
                      const plainTextValue =
                        value != null && typeof value !== 'object'
                          ? String(value)
                          : undefined;

                      let cellContent;
                      if (hasClickableConfig && column.clickable) {
                        cellContent = (
                          <ClickableCell
                            href={column.clickable.getHref(row)}
                            title={plainTextValue}
                            stopPropagation={
                              column.clickable.stopPropagation !== false
                            }
                            addon={column.clickable.renderAddon?.(row)}
                            target={column.clickable.target}
                          >
                            {String(value)}
                          </ClickableCell>
                        );
                      } else if (shouldTruncate) {
                        if (!hasCustomRenderer && plainTextValue) {
                          cellContent = (
                            <div className="truncate" title={plainTextValue}>
                              {String(value)}
                            </div>
                          );
                        } else {
                          cellContent = (
                            <div className="truncate">
                              {hasCustomRenderer && column.cell
                                ? column.cell(row, value)
                                : String(value)}
                            </div>
                          );
                        }
                      } else if (hasCustomRenderer && column.cell) {
                        cellContent = column.cell(row, value);
                      } else {
                        cellContent = String(value);
                      }

                      return (
                        <TableCell
                          key={`${getKey(row, rowIndex)}-${column.id}`}
                          className={cellClassName}
                          style={
                            density === 'compact'
                              ? { paddingTop: '4px', paddingBottom: '4px' }
                              : undefined
                          }
                        >
                          {cellContent}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );

                // Wrap with context menu if handler provides items
                const hasContextMenu =
                  contextMenuItems && contextMenuItems.length > 0;

                const finalRowContent = hasContextMenu ? (
                  <ContextMenu key={rowKey}>
                    <ContextMenuTrigger asChild>
                      {rowContent}
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      {contextMenuItems.map((item, index) => (
                        <ContextMenuItem key={index} onClick={item.onClick}>
                          {item.icon && (
                            <span className="mr-2">{item.icon}</span>
                          )}
                          {item.label}
                        </ContextMenuItem>
                      ))}
                    </ContextMenuContent>
                  </ContextMenu>
                ) : (
                  rowContent
                );

                // Wrap with optional group header
                if (groupHeader) {
                  return (
                    <React.Fragment key={`grouped-${rowKey}`}>
                      {groupHeader}
                      {finalRowContent}
                    </React.Fragment>
                  );
                }

                return finalRowContent;
              });
              })()}
          </TableBody>
        </Table>

        {/* Empty state overlay - positioned to stay centered in visible viewport */}
        {!isLoading && filteredData.length === 0 && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ top: density === 'compact' ? '33px' : '41px' }} // Offset for header height
          >
            <div className="pointer-events-auto">
              {emptyStateConfig ? (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  {/* Icon */}
                  {emptyStateConfig.icon && (
                    <div className="mb-4">{emptyStateConfig.icon}</div>
                  )}

                  {/* Title */}
                  <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-1">
                    {emptyStateConfig.title || emptyMessage}
                  </h3>

                  {/* Description */}
                  {emptyStateConfig.description && (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center max-w-sm mb-4">
                      {emptyStateConfig.description}
                    </p>
                  )}

                  {/* Action button */}
                  {emptyStateConfig.action && (
                    <Button
                      outline
                      onClick={emptyStateConfig.action.onClick}
                      className="mt-2"
                    >
                      {emptyStateConfig.action.label}
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <Text className="text-zinc-500 dark:text-zinc-400">
                    {emptyMessage}
                  </Text>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {paginationEnabled && filteredData.length > 0 && (
        <div className="flex-shrink-0">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={onPageChange || (() => {})}
            onPageSizeChange={onPageSizeChange || (() => {})}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
};
